import { Car, MessageCircle, HandCoins } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  ValuationResponse,
  VehicleEstimationResponse,
} from "@/types/valuation";
import { VehicleDetails } from "@/types/vehicleDetails";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";

interface EstimatedPriceLabels {
  thanksForValuation: string;
  weHaveInfo: string;
  weWillContact: string;
  estimatedTradeIn: string;
  estimatedValuation: string;
  horsePower: string;
}

interface EstimatedPriceProps {
  vehicleInfo: Partial<VehicleDetails>;
  valuationData?: ValuationResponse | VehicleEstimationResponse;
  labels?: EstimatedPriceLabels;
}

export const EstimatedPrice = ({
  vehicleInfo,
  valuationData,
  labels: labelsProp,
}: EstimatedPriceProps) => {
  const { language } = useLanguage();
  const allValuationLabels = valuationLabels(language);
  const vLabels = allValuationLabels.vehicleInfo;
  const expectations = allValuationLabels.expectations;
  const opts =
    (vLabels as { options?: Record<string, Record<string, string>> }).options ??
    {};
  const getFuelLabel = (key: string) => opts.fuelType?.[key] ?? key;

  const labels = labelsProp || {
    thanksForValuation: expectations.thanksForValuation,
    weHaveInfo: expectations.weHaveInfo,
    weWillContact: expectations.weWillContact,
    estimatedTradeIn: expectations.estimatedTradeIn,
    estimatedValuation: expectations.estimatedValuation,
    horsePower: expectations.horsePower,
  };
  // Handle VehicleEstimationResponse format (from new valuation endpoints)
  const isVehicleEstimationResponse =
    valuationData && "estimatedMarketValue" in valuationData;
  const purchasePrice = isVehicleEstimationResponse
    ? (valuationData as VehicleEstimationResponse).purchasePrice
    : undefined;

  return (
    <>
      <div className="text-center mb-6">
        <h2 className="text-lg md:text-2xl font-bold text-green-600 mb-2">
          {labels.thanksForValuation}
        </h2>
        <p className="text-gray-600">{labels.weHaveInfo}</p>
      </div>

      <div className="text-sm flex items-center gap-2 p-3 rounded-lg">
        <MessageCircle className="h-5 w-5 text-blue-500" />
        {labels.weWillContact}
      </div>

      <div>
        <Card className="flex p-4 flex-col gap-4 mt-4">
          <div className="flex items-center gap-4">
            <Car className="h-6 w-6 text-primary/60" />
            <div>
              <p className="text-sm md:text-lg font-medium">
                {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
              </p>
              <p className="text-sm md:text-lg text-primary/60">
                {vehicleInfo.fuelType === "El" ||
                vehicleInfo.fuelType === "Elbil" ||
                vehicleInfo.fuelType === "ELECTRIC"
                  ? `${vehicleInfo.horsepower} ${labels.horsePower}, ${vehicleInfo.fuelType ? getFuelLabel(vehicleInfo.fuelType) : ""}`
                  : [
                      vehicleInfo.engine,
                      vehicleInfo.fuelType
                        ? getFuelLabel(vehicleInfo.fuelType)
                        : "",
                    ]
                      .filter(Boolean)
                      .join(", ")}
              </p>
            </div>
          </div>
          <div className="flex flex-row items-center gap-4">
            <HandCoins className="h-6 w-6 text-primary/60" />
            <div>
              <p className="text-xl md:text-3xl font-bold text-accent">
                {purchasePrice !== undefined
                  ? `${purchasePrice.toLocaleString()} kr`
                  : null}
              </p>
              <div className="flex items-center gap-1">
                <p className="text-sm md:text-lg text-primary/60">
                  {labels.estimatedValuation}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};
